import './SetupAuditTrailGraph.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncStateWithPreloadHook } from '@lib/hooks';
import { DateRangePickerParams } from '@ui/DateRangePicker';
import { DateRangeQueryParams } from '@sonar-software/sonar-ts-types';
import { getPastDate } from '@sonar-software/sonar-ts-lib';
import { SonarText } from '@ui/text';

import { ClassificationLevel, coreActions } from '@sonar-software/sonar-core';
import { Flex, Tag } from 'antd';
import { LoadingDots, SonarCard } from '@ui/general';
import React, { useEffect, useMemo, useState } from 'react';
import { useAction, usePrevious, useSonarSelector } from '@hooks';

const classificationColors: Record<ClassificationLevel, string> = {
	[ ClassificationLevel.Critical ]: '#cf1322',
	[ ClassificationLevel.High ]: '#d46b08',
	[ ClassificationLevel.Low ]: '#722ed1',
	[ ClassificationLevel.Medium ]: '#fadb14',
	[ ClassificationLevel.None ]: '#bfbfbf'
};

const sortedClassifications : Array<ClassificationLevel> = [
	ClassificationLevel.Critical,
	ClassificationLevel.High,
	ClassificationLevel.Medium,
	ClassificationLevel.Low,
	ClassificationLevel.None
];

const getLegendText = (name: ClassificationLevel, value: number) =>
	`${name === 'null' ? COPY_SPECIFIC.NO_CLASSIFICATION : name} (${value})`;


const defaultParams: DateRangeQueryParams = {
	start: getPastDate(7)
};

const useAuditTrailGraphData = createAsyncStateWithPreloadHook({
	defaultRequest: defaultParams,
	loadAction: coreActions.requestGetCoreDataSecuritySetupAuditTrailGraph,
	selector: (state) => state.core.dataSecurity.setupAuditTrail.graph.get,
	useTransform: (result) => result.data
});

export interface SetupAuditTrailGraphProps {
	classificationsFilter: Array<ClassificationLevel>;
	dateRange: DateRangePickerParams;
	onClassificationFilterChange: (classifications: Array<ClassificationLevel>) => void;
}

const titleStart = `${COPY_SPECIFIC.CHANGES} (`;
const titleEnd = ')';

export const SetupAuditTrailGraph: React.FC<SetupAuditTrailGraphProps> = ({
	classificationsFilter,
	dateRange,
	onClassificationFilterChange
}) => {
	const previousRange = usePrevious(dateRange, dateRange);
	const [ hoverClassification, setHoverClassification ] = useState<ClassificationLevel | null>(null);

	const data = useAuditTrailGraphData(true, {
		...defaultParams,
		...dateRange
	});

	const loadData = useAction(useAuditTrailGraphData.loadAction);
	const isLoading = useSonarSelector(useAuditTrailGraphData.selectors.isRequested);

	const totalChanges = data?.changeCount ?? 0;

	const title = isLoading ?
		<div className='graph-title'>{ titleStart }<LoadingDots />{ titleEnd }</div> :
		`${titleStart}${totalChanges}${titleEnd}`;

	useEffect(() => {
		if (dateRange.end !== previousRange?.end || dateRange.start !== previousRange?.start) {
			loadData(dateRange);
		}
	}, [ dateRange, loadData, previousRange?.end, previousRange?.start ]);

	const classificationTotalLookup = useMemo(() => {
		if (!data) {
			return {} as Record<ClassificationLevel, number>;
		}

		return data.classifications.reduce((prev, next) => {
			prev[ next.classification ] = next.total;

			return prev;
		}, {} as Record<ClassificationLevel, number>);
	}, [ data ]);

	const handleClick = (classification: ClassificationLevel) => {
		if (classificationsFilter.includes(classification)) {
			onClassificationFilterChange(classificationsFilter.filter((x) => x !== classification));
		} else {
			onClassificationFilterChange([ ...classificationsFilter, classification ]);
		}
	};

	const isClassificationActive = (classification: ClassificationLevel) => {
		if (hoverClassification) {
			if (classification === hoverClassification) {
				return true;
			}

			return classificationsFilter.length && classificationsFilter.includes(classification);
		}

		return !classificationsFilter.length || classificationsFilter.includes(classification);
	};

	return (
		<SonarCard
			className='setup-audit-trail-graph'
			loading={ isLoading }
			title={ title }
			titleClassName='graph-title'>
			<Flex
				gap={ 16 }
				vertical>
				<Flex align='center'>
					{
						sortedClassifications.map((classification) => {
							const total = classificationTotalLookup[ classification ];

							if (!total) {
								return null;
							}

							return (
								<Tag
									className='graph-segment'
									color={ classificationColors[ classification ] }
									key={ classification }
									onClick={ () => handleClick(classification) }
									onMouseEnter={ () => setHoverClassification(classification) }
									onMouseLeave={ () => setHoverClassification(null) }
									style={ {
										opacity: isClassificationActive(classification) ? 1 : 0.4,
										width: `${totalChanges ? (total / totalChanges) * 100 : 0}%`
									} } />
							);
						})
					}
				</Flex>
				<Flex
					align='center'
					className='graph-legend'
					gap={ 16 }>
					{
						sortedClassifications.map((classification) => {
							const total = classificationTotalLookup[ classification ];

							return (
								<Flex
									align='center'
									key={ classification }
									style={ { opacity: isClassificationActive(classification) ? 1 : 0.4 } }>
									<Tag color={ classificationColors[ classification ] } />
									<SonarText>{ getLegendText(classification, total ?? 0) }</SonarText>
								</Flex>
							);
						})
					}
				</Flex>
			</Flex>
		</SonarCard>
	);
};
